"use strict";
/**
 * REVENUE SUB-HEAD CODES — how a blank one is filled in.
 *
 * The Account Code master keeps a stable code because revenue is REPORTED by
 * sub head, and a report groups by the code rather than by the caption. That
 * requirement is about the stored row, not about the operator: asking someone
 * to invent "CFOP" mid-form, while an account head sits half-entered behind
 * the dialog, buys the dimension nothing. So the field is optional and the
 * code is DERIVED from the name when it is left blank.
 *
 * This lives in `contracts`, like `charge-tier-pricing` and
 * `open-value-pricing`, because two places need the same answer: the BROWSER
 * previews what blank will produce, and the SERVER produces it. A second copy
 * of the strip-and-truncate rule in React is precisely the drift this file
 * prevents — the preview would quietly stop matching the saved row, and only a
 * report would ever show it.
 *
 * WHAT IS NOT HERE: collision handling. The stem may already be taken, and only
 * the server can know that; it appends a counter within the same 10-character
 * budget (`accountCodeCandidates`). The browser therefore previews the STEM as
 * a placeholder hint and never as a promise.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.ACCOUNT_CODE_MAX_LENGTH = exports.ACCOUNT_CODE_PATTERN = void 0;
exports.deriveAccountCodeStem = deriveAccountCodeStem;
exports.accountCodeCandidates = accountCodeCandidates;
exports.accountCodeScanPrefix = accountCodeScanPrefix;
/** Exactly the shape the `account_codes_code_format` CHECK constraint enforces. */
exports.ACCOUNT_CODE_PATTERN = /^[A-Z0-9]{2,10}$/;
/** The DB column is `VarChar(10)`, and the CHECK repeats the bound. */
exports.ACCOUNT_CODE_MAX_LENGTH = 10;
/**
 * The code a name derives to before any collision check:
 * `"CONS FEE - OP"` -> `"CONSFEEOP"`, `"Bio-Chemistry"` -> `"BIOCHEMIST"`.
 *
 * A name with fewer than two usable characters gets a `SUB` prefix rather than
 * a short value: the CHECK constraint refuses anything under two characters,
 * and a derived code must never be able to fail a constraint the operator who
 * left the field blank cannot see. Returns `''` for a blank name — there is
 * nothing to derive from, and the caller is already rejecting that.
 */
function deriveAccountCodeStem(name) {
    const stripped = name.toUpperCase().replace(/[^A-Z0-9]/g, '');
    if (!stripped)
        return '';
    return (stripped.length >= 2 ? stripped : `SUB${stripped}`).slice(0, exports.ACCOUNT_CODE_MAX_LENGTH);
}
/**
 * The stem, then the suffixed variants tried in order when it is taken —
 * `CONSFEEOP`, `CONSFEEOP2`, … The counter is spent INSIDE the 10-character
 * budget (the tail is trimmed, not the digits), because the column cannot grow.
 */
function accountCodeCandidates(name) {
    const stem = deriveAccountCodeStem(name);
    if (!stem)
        return [];
    const candidates = [stem];
    for (let n = 2; n <= 99; n += 1) {
        const suffix = String(n);
        candidates.push(`${stem.slice(0, exports.ACCOUNT_CODE_MAX_LENGTH - suffix.length)}${suffix}`);
    }
    return candidates;
}
/**
 * The prefix that covers every candidate above, for the server's single
 * "what is already taken" scan. Eight characters: a 1-2 digit suffix can only
 * ever replace the last one or two, so nothing shorter is needed and nothing
 * longer would match the suffixed forms.
 */
function accountCodeScanPrefix(name) {
    return deriveAccountCodeStem(name).slice(0, exports.ACCOUNT_CODE_MAX_LENGTH - 2);
}
//# sourceMappingURL=account-code-derivation.js.map